import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { Spin } from 'antd';
import { hasAuthParams, useAuth } from 'react-oidc-context';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEmailVerified } from 'src/hooks/use-email-verified';
import { getRoles, J2LRole, J2LRoles } from 'src/shared/utils/user';
import { setPostLoginUrl } from 'src/shared/configs/auth-config';

type Mode = 'require' | 'redirectIfVerified';

type Props = {
  children: ReactNode;
  mode: Mode;
  successPath?: string;
  verifyPath?: string;
  onlyForRoles?: J2LRoles[];
};

const EmailVerificationGuard: FC<Props> = ({
  children,
  mode,
  successPath = '/default-user-page',
  verifyPath = '/onboarding/verify-email',
  onlyForRoles = [J2LRole.EnterpriseAdmin]
}) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [pending, setPending] = useState(true);
  const [redirectingToLogin, setRedirectingToLogin] = useState(false);

  const { check } = useEmailVerified({ user: auth.user, auto: false });

  const currentUrl = useMemo(
    () => `${location.pathname}${location.search ?? ''}${location.hash ?? ''}`,
    [location.pathname, location.search, location.hash]
  );

  const roles = useMemo(() => (getRoles(auth.user) || []) as J2LRoles[], [auth.user]);
  const appliesToUser = useMemo(() => onlyForRoles.length === 0 || roles.some((r) => onlyForRoles.includes(r)), [onlyForRoles, roles]);

  const successUrl = useMemo(() => `${successPath}${location.search ?? ''}`, [successPath, location.search]);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        if (auth.isLoading || auth.activeNavigator) {
          if (mounted) setPending(true);
          return;
        }

        if (!auth.isAuthenticated || !auth.user) {
          if (hasAuthParams()) {
            if (mounted) setPending(true);
            return;
          }
          if (!redirectingToLogin && auth.signinRedirect) {
            setRedirectingToLogin(true);
            setPostLoginUrl(`${window.REACT_APP_API_BASE_URI}${currentUrl}`);
            await auth.signinRedirect();
          }
          return;
        }

        if (!appliesToUser) {
          if (mode === 'redirectIfVerified') {
            navigate(successUrl, { replace: true });
            return;
          }
          if (mounted) setPending(false);
          return;
        }

        const isVerified = await check();
        if (!mounted) return;

        if (mode === 'require') {
          if (!isVerified) {
            navigate(verifyPath, { replace: true });
            return;
          }
          setPending(false);
          return;
        }

        if (mode === 'redirectIfVerified') {
          if (isVerified) {
            navigate(successUrl, { replace: true });
            return;
          }
          setPending(false);
          return;
        }
      } catch {
        if (mounted) setPending(false);
      }
    })();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    auth.isLoading,
    auth.activeNavigator,
    auth.isAuthenticated,
    auth.user,
    appliesToUser,
    mode,
    verifyPath,
    successUrl,
    currentUrl,
    redirectingToLogin
  ]);

  if (pending) return <Spin size="large" className="global-spinner" spinning />;

  return <>{children}</>;
};

export default EmailVerificationGuard;
